<template>
  <v-card class="rounded-lg ml-2 mb-2" elevation="12">
    <h2 class="tw-text-center mt-2 mb-1">Activity</h2>
    <v-col class="mx-auto d-flex justify-center flex-column align-center pt-0 pb-1">
      <v-select
        name="activity"
        v-model="selectedActivityPeriod"
        :items="activityPeriods"
        item-text="display"
        item-value="value"
        outlined
        dense
        hide-details
      />
    </v-col>
    <v-row class="mb-1 justify-center">
      <v-col align="center" cols="12" xl="4" class="mx-4 justify-center pb-0">
        <DashboardCard
          text="Transactions"
          :value="numberFormattedValue(periodData.transactions)"
        />
      </v-col>
      <v-col align="center" cols="12" xl="4" class="mx-4 justify-center pb-0">
        <DashboardCard text="Volume" :value="dollarFormattedValue(periodData.volume)" />
      </v-col>
    </v-row>
    <h2 class="tw-text-center mb-0">
      {{ selectedActivityPeriod === 'thisYear' ? 'Monthly' : 'Daily' }} Sales
    </h2>
    <div class="card-container vertical">
      <div class="card dashboard_line_chart_container pt-0">
        <LineChart :chart-data="chartData" :options="chartOptions" :height="lineChartHeight" />
      </div>
      <div class="mt-4 ml-4">
        <p>
          <a
            class="link-text"
            name="customer-shipping-link"
            href @click.prevent="toggleTransactionsTable"
          >
            {{ transactionsTableText }}
          </a>
        </p>
      </div>
      <div v-if="showTransactionsTable" class="card">
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr class="main-tr">
                <th></th>
                <th>Previous</th>
                <th>Previous</th>
                <th>Current</th>
                <th>Current</th>
              </tr>
              <tr>
                <th>Date</th>
                <th>Transactions</th>
                <th>Volume</th>
                <th>Transactions</th>
                <th>Volume</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(date, j) in maxDatesInCurrentOrPrevious" :key="j">
                <td>{{ dateFormattedValue(date, dateFormat) }}</td>
                <td class="tw-text-gray-500">
                  {{ previousPeriodDataAt(j, 'transactions') | simpleNumber }}
                </td>
                <td class="tw-text-gray-500">
                  {{ formattedPreviousPeriodDataAt(j, 'volume') }}&nbsp;
                </td>
                <td>{{ currentPeriodDataAt(j, 'transactions') | simpleNumber }}&nbsp;</td>
                <td>{{ formattedCurrentPeriodDataAt(j, 'volume') }}&nbsp;</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </div>
  </v-card>
</template>

<script>
import DashboardCard from '@/common/components/DashboardCard.vue';
import { DateTime } from 'luxon';
import LineChart from '@/common/components/charts/LineChart';
import { dateFormattedValue, dollarFormattedValue, numberFormattedValue } from '@/common/util/dashboard.util';

export default {
  data() {
    return {
      showTransactionsTable: false,
      selectedActivityPeriod: this.activityPeriod,
      lineChartHeight: 300,
    };
  },
  props: {
    activityPeriod: {
      required: true,
    },
    data: {
      required: true,
    },
  },
  computed: {
    transactionsTableText() {
      return this.showTransactionsTable ? 'Hide transactions table' : 'Show transactions table';
    },
    chartData() {
      return {
        labels: [...this.maxDatesInCurrentOrPrevious
          .map((date) => this.dateFormattedValue(date, this.dateFormat))],
        datasets: [
          {
            label: 'Current Period Volume',
            data: [...this.currentPeriodData.map(({ volume }) => volume / 100)],
            backgroundColor: 'rgba(0,255,0,0.5)',
          },
          {
            label: 'Previous Period Volume',
            data: [...this.previousPeriodData.map(({ volume }) => volume / 100)],
            backgroundColor: 'rgba(0,0,255,0.5)',
          },
        ],
      };
    },
    activityPeriods() {
      return [
        { value: 'thisMonth', display: 'Month To Date' },
        { value: 'lastMonth', display: 'Previous Month' },
        { value: 'thisYear', display: 'Year To Date' },
      ];
    },
    periodData() {
      return this.data[this.selectedActivityPeriod];
    },
    previousPeriodData() {
      return this.periodData.previous;
    },
    currentPeriodData() {
      return this.periodData.current;
    },
    dateFormat() {
      return this.selectedActivityPeriod === 'thisYear' ? 'mmm' : 'dS';
    },
    maxDatesInCurrentOrPrevious() {
      if (this.selectedActivityPeriod !== 'thisYear') {
        const maxDays = Math.max(
          DateTime.fromISO(this.previousPeriodData[0].date).daysInMonth,
          DateTime.fromISO(this.currentPeriodData[0].date).daysInMonth,
        );
        return Array.from(
          { length: maxDays },
          (value, idx) => DateTime.fromObject({ month: 1, day: idx + 1 }).toISODate(),
        );
      }

      const entries = this.currentPeriodData.length >= this.previousPeriodData.length
        ? this.currentPeriodData
        : this.previousPeriodData;

      return entries.map(({ date }) => date);
    },
    chartOptions() {
      const that = this;

      return {
        tooltips: {
          callbacks: {
            title: () => {},
            label(item) {
              const originalSet = item.datasetIndex === 0
                ? that.currentPeriodData : that.previousPeriodData;
              const originalItem = originalSet[item.index];
              const date = that.dateFormattedValue(originalItem?.date, 'shortDate');
              const volume = that.dollarFormattedValue(originalItem?.volume);
              const transactions = that.$options.filters.simpleNumber(originalItem?.transactions);

              return `${date ? `${date}:` : ''} ${volume || 0} (${transactions || 0})`;
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
      };
    },
  },
  methods: {
    dateFormattedValue,
    dollarFormattedValue,
    numberFormattedValue,
    previousPeriodDataAt(idx, key) {
      return this.periodDataAt('previous', idx, key);
    },
    formattedPreviousPeriodDataAt(idx, key) {
      return this.dollarFormattedValue(this.previousPeriodDataAt(idx, key));
    },
    currentPeriodDataAt(idx, key) {
      return this.periodDataAt('current', idx, key);
    },
    formattedCurrentPeriodDataAt(idx, key) {
      return this.dollarFormattedValue(this.currentPeriodDataAt(idx, key));
    },
    periodDataAt(period, idx, key) {
      if (!this.periodData[period][idx]) {
        return null;
      }
      return this.periodData[period][idx][key];
    },
    toggleTransactionsTable() {
      this.showTransactionsTable = !this.showTransactionsTable;
    },
  },
  components: {
    DashboardCard,
    LineChart,
  },
};
</script>
